'use client';

import { ReactNode, useMemo } from 'react';
import { VStack, Text, StackProps } from '@chakra-ui/react';

import { useGiftcard } from 'context/giftcard';
import { useGiftcardsPagesLayoutContent } from 'context/giftcardsPagesLayoutContent';

import { getGiftcardAmountSubtitle } from './utils';
import { useAppLayoutContext } from './AppLayoutContext';

export interface GiftcardAmountProps extends StackProps {
  subtitle?: ReactNode;
  value?: string;
}

export const GiftcardAmount = (props: GiftcardAmountProps) => {
  const { subtitle = '', value, color, ...rest } = props;

  const { giftcard } = useGiftcard();

  const { subtitles } = useGiftcardsPagesLayoutContent();

  const { variant } = useAppLayoutContext();

  const { amount, currency, amountSubtitle } = useMemo(() => {
    if ((!giftcard && !value) || !subtitles) {
      return {
        availableBalance: '',
        currencySignal: '',
        amountSubtitle: '\u00A0',
      };
    }

    const formattedAmount = value || giftcard.formatted.availableBalance;

    const [_amount, _currency] = formattedAmount.split(/\s/);

    return {
      amount: _amount,
      currency: _currency,
      amountSubtitle:
        subtitle || getGiftcardAmountSubtitle({ giftcard, subtitles }),
    };
  }, [giftcard]);

  return (
    <VStack
      pb={['0.5rem', '0.5rem', '1.25rem']}
      data-testid="giftcard-amount-title"
      color={variant === 'error' ? 'white' : color}
      {...rest}
    >
      <Text fontSize="4rem" fontFamily="Roboto Slab" fontWeight="bold">
        {amount}
        <Text as="span" fontSize="2.8rem" ml="0.75rem">
          {currency}
        </Text>
      </Text>

      <Text fontWeight={300}>{subtitle || amountSubtitle}</Text>
    </VStack>
  );
};
